/**
 * AudioSpeechSection — Autoplay toggle, microphone test
 */

import React from 'react';
import { Switch } from 'react-native';
import {
  VStack,
  HStack,
  Text,
  Pressable,
} from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import Ionicons from '../ui/Ionicons';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { FS_XS, FS_SM, FS_MD } from '../../theme';
import { SectionCard } from '../ui';
import { useAudioMode } from '../../hooks/useAudioMode';
import { SPEECH_RECOGNITION_AVAILABLE } from '../../config/speechCapabilities';

export const AudioSpeechSection = React.memo(function AudioSpeechSection() {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();
  const navigation = useNavigation('Profile');

  const { autoplay, toggle: toggleAutoplay } = useAudioMode();

  return (
    <SectionCard title={t('profile.audioSpeech.title')}>
      {/* Autoplay toggle */}
      <HStack justifyContent="space-between" alignItems="center">
        <VStack flex={1} mr="$3">
          <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}>
            {t('profile.audioSpeech.autoplayTitle')}
          </Text>
          <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}>
            {t('profile.audioSpeech.autoplayDescription')}
          </Text>
        </VStack>
        <Switch
          testID="profile-toggle-autoplay"
          accessibilityLabel={t('profile.audioSpeech.autoplayTitle')}
          accessibilityRole="switch"
          value={autoplay}
          onValueChange={toggleAutoplay}
        />
      </HStack>

      {/* Microphone test \u2014 the recognizer relay is native-only (server-speech-proxy
          Phase 2), so on web this becomes a non-navigating "not available yet" row. */}
      {SPEECH_RECOGNITION_AVAILABLE ? (
        <Pressable
          testID="profile-button-mic-test"
          accessibilityLabel={t('profile.audioSpeech.micTest')}
          accessibilityRole="button"
          onPress={() => navigation.navigate('MicrophoneTest')}
        >
          <HStack
            style={{ backgroundColor: theme.inputBg }}
            p="$4"
            rounded="$lg"
            space="sm"
            justifyContent="space-between"
            alignItems="center"
          >
            <HStack space="sm" alignItems="center" style={{ flex: 1 }}>
              <Text allowFontScaling={false} style={{ fontSize: 20 }}>{'\uD83C\uDFA4'}</Text>
              <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, fontWeight: '600', color: theme.text, ...ffs }}>
                {t('profile.audioSpeech.micTest')}
              </Text>
            </HStack>
            <Ionicons name="chevron-forward" size={18 * fontScale} color={theme.textSecondary} />
          </HStack>
        </Pressable>
      ) : (
        <HStack
          testID="profile-row-mic-test-unavailable"
          accessibilityLabel={t('profile.audioSpeech.micTestWebUnavailable')}
          style={{ backgroundColor: theme.inputBg, opacity: 0.6 }}
          p="$4"
          rounded="$lg"
          space="sm"
          alignItems="center"
        >
          <Text allowFontScaling={false} style={{ fontSize: 20 }}>{'\uD83C\uDFA4'}</Text>
          <VStack flex={1}>
            <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, fontWeight: '600', color: theme.text, ...ffs }}>
              {t('profile.audioSpeech.micTest')}
            </Text>
            <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}>
              {t('profile.audioSpeech.micTestWebUnavailable')}
            </Text>
          </VStack>
        </HStack>
      )}
    </SectionCard>
  );
});
